import { supabase } from "@/lib/supabase";

export interface AvailabilityBlock {
  id: number;
  scope: string;
  target: string | null;
  start_date: string;
  end_date: string;
  block_type: string;
  reason: string | null;
  created_at: string;
}

export interface AvailabilityBlockInput {
  scope: string;
  target?: string | null;
  start_date: string;
  end_date: string;
  block_type?: string;
  reason?: string | null;
}

export interface AvailabilitySlot {
  id: number;
  scope: string;
  target: string | null;
  weekday: number;
  start_time: string;
  end_time: string;
  capacity: number;
  min_guests: number;
  max_guests: number;
  active: boolean;
  created_at: string;
}

export interface AvailabilitySlotInput {
  scope: string;
  target?: string | null;
  weekday: number;
  start_time: string;
  end_time: string;
  capacity: number;
  min_guests: number;
  max_guests: number;
  active: boolean;
}

export const BLOCK_TYPE_META: Record<string, { label: string; chip: string; dot: string }> = {
  blocked: {
    label: "Blocked",
    chip: "bg-foreground-100 text-foreground-700 border-foreground-200",
    dot: "bg-foreground-500",
  },
  maintenance: {
    label: "Maintenance",
    chip: "bg-accent-100 text-accent-900 border-accent-300",
    dot: "bg-accent-500",
  },
  holiday: {
    label: "Holiday",
    chip: "bg-secondary-100 text-secondary-700 border-secondary-300",
    dot: "bg-secondary-500",
  },
};

export function blockTypeMeta(type: string) {
  return BLOCK_TYPE_META[type] || BLOCK_TYPE_META.blocked;
}

export async function fetchBlocks(): Promise<AvailabilityBlock[]> {
  const { data, error } = await supabase
    .from("availability_blocks")
    .select("*")
    .order("start_date", { ascending: true });
  if (error) throw error;
  return (data as AvailabilityBlock[]) || [];
}

export async function createBlock(input: AvailabilityBlockInput): Promise<AvailabilityBlock> {
  const { data, error } = await supabase
    .from("availability_blocks")
    .insert({
      scope: input.scope,
      target: input.target || null,
      start_date: input.start_date,
      end_date: input.end_date,
      block_type: input.block_type || "blocked",
      reason: input.reason || null,
    })
    .select("*")
    .maybeSingle();
  if (error) throw error;
  return data as AvailabilityBlock;
}

export async function deleteBlock(id: number): Promise<void> {
  const { error } = await supabase.from("availability_blocks").delete().eq("id", id);
  if (error) throw error;
}

export async function fetchSlots(): Promise<AvailabilitySlot[]> {
  const { data, error } = await supabase
    .from("availability_slots")
    .select("*")
    .order("weekday", { ascending: true })
    .order("start_time", { ascending: true });
  if (error) throw error;
  return (data as AvailabilitySlot[]) || [];
}

export async function createSlot(input: AvailabilitySlotInput): Promise<AvailabilitySlot> {
  const { data, error } = await supabase
    .from("availability_slots")
    .insert(input)
    .select("*")
    .maybeSingle();
  if (error) throw error;
  return data as AvailabilitySlot;
}

export async function updateSlot(
  id: number,
  patch: Partial<AvailabilitySlotInput>,
): Promise<void> {
  const { error } = await supabase.from("availability_slots").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteSlot(id: number): Promise<void> {
  const { error } = await supabase.from("availability_slots").delete().eq("id", id);
  if (error) throw error;
}

export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];